import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X, Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'gdpr_consent';

export const GdprBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // Short delay so it doesn't flash on first render
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = (type) => {
    localStorage.setItem(STORAGE_KEY, type);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="fixed bottom-4 left-4 right-4 z-[9999] max-w-2xl mx-auto"
          role="dialog"
          aria-labelledby="gdpr-title"
          aria-describedby="gdpr-desc"
          data-testid="gdpr-banner"
        >
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl shadow-2xl shadow-gray-300/40 dark:shadow-black/50 overflow-hidden">

            {/* Main row */}
            <div className="px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Cookie className="w-4.5 h-4.5 text-emerald-600" />
                </div>

                <div className="flex-1 min-w-0">
                  <p id="gdpr-title" className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
                    portion.bg използва бисквитки
                  </p>
                  <p id="gdpr-desc" className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Използваме задължителни бисквитки за работата на сайта. С „Приемам всички" се съгласявате и с функционални бисквитки за подобряване на преживяването.{' '}
                    <Link to="/privacy" className="text-emerald-600 hover:underline font-medium" onClick={() => setVisible(false)}>
                      Политика за поверителност
                    </Link>
                  </p>
                </div>

                <button
                  onClick={() => accept('minimal')}
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                  aria-label="Затвори"
                  data-testid="gdpr-close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Expanded details */}
              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700 space-y-2">
                      {[
                        { label: 'Задължителни', desc: 'Необходими за вход и функциониране на сайта. Не могат да бъдат деактивирани.', always: true },
                        { label: 'Функционални', desc: 'Запомнят предпочитанията Ви (данни от калкулатора, кеш на плана). Изтриват се при край на сесията.', always: false },
                      ].map(item => (
                        <div key={item.label} className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[12px] font-semibold text-gray-700 dark:text-gray-300">{item.label}</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">{item.desc}</p>
                          </div>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${item.always ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-600'}`}>
                            {item.always ? 'Винаги' : 'По избор'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions row */}
              <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                  <button
                  onClick={() => setExpanded(e => !e)}
                  className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  data-testid="gdpr-expand"
                >
                  {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {expanded ? 'По-малко' : 'Подробности'}
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => accept('minimal')}
                    className="px-3.5 py-1.5 text-[12px] font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    data-testid="gdpr-reject"
                  >
                    Само необходими
                  </button>
                  <button
                    onClick={() => accept('all')}
                    className="px-3.5 py-1.5 text-[12px] font-semibold text-white bg-emerald-600 rounded-full hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
                    data-testid="gdpr-accept"
                  >
                    <ShieldCheck className="w-3 h-3" />
                    Приемам всички
                  </button>
                </div>
              </div>
            </div>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
